import React, { useState } from 'react';
import { Box, Typography, Grid, Stack, Checkbox, IconButton, Tooltip } from '@mui/material';
import { Add, EmojiEvents, Sync, Delete, WarningAmber } from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import CustomButton from '../../../Button/CustomButton';
import { useDispatch } from 'react-redux';
import { createAwardCategory, deleteAwardCategory, updateAwardCategory } from '../../../../redux/contest/actions';
import CreateAwardCategoryDialog from './CreateAwardCategoryDialog';
import AwardCategoryCard from './AwardCategoryCard';
import { IContestManageResponse } from '../../../../redux/contest/types';
import { IAwardCategory } from '../../../../redux/contest/types';
import { TSubmissionCategoryAward } from '../../../../types/global/helper';
import { LaurelCreatorModal } from '../../../LaurelCreator/LaurelCreatorModal';
import { groupAwardsByTierPosition, showToastMessage, delay, useDispatchPromise } from '../../../../utils/AppUtils';
import {
    getAwardPrizeCents,
    prizeDollarsToApiCents,
} from '../../../../pages/BrandDashboard/contestWizard/buildAwardCategoryPayload';
import { isEmpty } from '../../../../utils/lodash';
import { DialogueSheet } from '../../../DialogueSheet/DialogueSheet';
import { getContestManage } from '../../../../redux/contest/actions';
import {
    brandAwardBodyTextSx,
    brandAwardEmptyStateSx,
    brandAwardWarningBannerSx,
    brandContestDangerIconButtonSx,
    brandContestPrimaryIconButtonSx,
    brandContestSecondaryIconButtonSx,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import { prepareAwardCategoryForEdit } from '../../../../pages/BrandDashboard/contestManage/contestCategoryWizardUtils';

interface ContestAwardsTabProps {
    contest: IContestManageResponse['contest'];
    hasSubmissions: boolean;
    awardCategories: IContestManageResponse['award_categories'];
    /** Brand contest manage drawer — icon actions with tooltips. */
    visualVariant?: 'default' | 'brand';
}

export const ContestAwardsTab: React.FC<ContestAwardsTabProps> = ({
    contest,
    hasSubmissions,
    awardCategories,
    visualVariant = 'default',
}) => {
    const { themeStyle } = useThemeContext();
    const isBrand = visualVariant === 'brand';
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedAward, setSelectedAward] = useState<IAwardCategory | null>(null);
    const [isLaurelCreatorOpen, setIsLaurelCreatorOpen] = useState(false);
    const [isMatchAllDialogOpen, setIsMatchAllDialogOpen] = useState(false);
    const [selectedAwardIds, setSelectedAwardIds] = useState<Set<string>>(new Set());
    const [isDeleteSelectedDialogOpen, setIsDeleteSelectedDialogOpen] = useState(false);

    // Helper to ensure categories is always an array
    const awardCategoryList: IAwardCategory[] = Array.isArray(awardCategories.categories)
        ? awardCategories.categories
        : awardCategories.categories
        ? [awardCategories.categories]
        : [];

    const handleSelectAward = (awardId: string, selected: boolean) => {
        setSelectedAwardIds(prev => {
            const newSet = new Set(prev);
            if (selected) {
                newSet.add(awardId);
            } else {
                newSet.delete(awardId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const allIds = new Set(awardCategoryList.map(award => award._id));
            setSelectedAwardIds(allIds);
        } else {
            setSelectedAwardIds(new Set());
        }
    };

    const isAllSelected = awardCategoryList.length > 0 && selectedAwardIds.size === awardCategoryList.length;
    const isIndeterminate = selectedAwardIds.size > 0 && selectedAwardIds.size < awardCategoryList.length;

    const handleDeleteSelected = async () => {
        if (selectedAwardIds.size === 0) return;

        const awardsToDelete = Array.from(selectedAwardIds);
        let successCount = 0;
        let errorCount = 0;

        for (const awardId of awardsToDelete) {
            try {
                // Find the category for this award
                const category = contest.categories?.find((cat: any) => {
                    if (cat.category_awards && Array.isArray(cat.category_awards)) {
                        return cat.category_awards.includes(awardId);
                    }
                    if (cat.with_tiered_pricing && cat.prize_structure) {
                        return cat.prize_structure.some((prize: any) => {
                            return Array.isArray(prize.payouts) && prize.payouts.includes(awardId);
                        });
                    }
                    return false;
                });

                let position: string | undefined;
                if (category?.with_tiered_pricing && category.prize_structure) {
                    for (const prize of category.prize_structure) {
                        if (Array.isArray(prize.payouts) && prize.payouts.includes(awardId as any)) {
                            position = prize.position;
                            break;
                        }
                    }
                }

                if (category) {
                    dispatch(
                        deleteAwardCategory({
                            documentId: awardId,
                            payload: {
                                categoryId: category._id,
                                contestId: contest._id,
                                position: position,
                            },
                        }),
                    );
                } else {
                    dispatch(
                        deleteAwardCategory({
                            documentId: awardId,
                            payload: {
                                position: position,
                            },
                        }),
                    );
                }

                // Wait for the saga to complete
                await delay(500);
                successCount++;
            } catch (error) {
                console.error('Error deleting award:', error);
                errorCount++;
            }
        }

        // Clear selection after deletion
        setSelectedAwardIds(new Set());

        if (successCount > 0) {
            showToastMessage(
                `Successfully deleted ${successCount} award(s).${errorCount > 0 ? ` ${errorCount} failed.` : ''}`,
                successCount === awardsToDelete.length ? 'success' : 'warning',
            );
        } else {
            showToastMessage('Failed to delete awards. Please try again.', 'error');
        }

        setIsDeleteSelectedDialogOpen(false);
    };

    const handleCreateAward = (awardData: any) => {
        if (selectedAward?._id) {
            /** This is edit */
            // Get old position and tier_label from selectedAward if they exist
            const oldPosition = (selectedAward as any).position;
            const oldTierLabel = (selectedAward as any).tier_label;

            // Preserve tier_fee ObjectId on tier_name (dialog resolves label → id).
            if (awardData.tier_label && awardData.category && !awardData.tier_name) {
                awardData.tier_position = awardData.position;
            }

            dispatch(
                updateAwardCategory({
                    documentId: selectedAward._id,
                    payload: awardData,
                    additionalPayload: {
                        oldPosition: oldPosition,
                        oldTierLabel: oldTierLabel,
                    },
                }),
            );
        } else {
            // Check if this is a new award creation and if there are existing awards
            if (awardCategoryList.length > 0 && contest.funded) {
                // Calculate total existing award amounts
                const existingTotal = awardCategoryList.reduce(
                    (total, award) => total + getAwardPrizeCents(award.prize),
                    0,
                );

                // Calculate new total with the new award (prize is integer cents on the server)
                let newTotal = existingTotal + getAwardPrizeCents(awardData.prize);
                const { positionEntries, totalMax } = groupAwardsByTierPosition(awardCategoryList);
                if (!isEmpty(positionEntries)) {
                    newTotal = totalMax;
                }

                const availableCents =
                    (contest.amount_available ?? 0) >= 10_000
                        ? getAwardPrizeCents(contest.amount_available)
                        : prizeDollarsToApiCents(contest.amount_available ?? 0);

                // Check if new total exceeds available escrow
                if (newTotal > availableCents) {
                    showToastMessage(
                        'Total award amount exceeds your current fund. Please fund your account first to add more awards.',
                        'error',
                    );
                    return; // Don't create the award
                }
            }

            dispatch(createAwardCategory(awardData));
        }
        setIsCreateDialogOpen(false);
        setSelectedAward(null);
    };

    const matchAllAwardsToCategories = () => {
        if (!contest.categories || contest.categories.length === 0) {
            showToastMessage('No categories found to match awards to.', 'error');
            return;
        }

        // Collect all awards from both awardCategoryList and prize_structure payouts
        const allAwards = new Map<string, IAwardCategory>();

        // Add awards from awardCategoryList
        awardCategoryList.forEach((award: IAwardCategory) => {
            if (award._id) {
                allAwards.set(award._id, award);
            }
        });

        // Also collect awards from prize_structure payouts (in case they're not in awardCategoryList)
        contest.categories?.forEach((category: any) => {
            if (category.with_tiered_pricing && Array.isArray(category.prize_structure)) {
                category.prize_structure.forEach((prize: any) => {
                    if (Array.isArray(prize.payouts)) {
                        prize.payouts.forEach((payout: any) => {
                            if (typeof payout === 'object' && payout !== null && payout._id) {
                                // Only add if not already in the map
                                if (!allAwards.has(payout._id)) {
                                    allAwards.set(payout._id, payout as IAwardCategory);
                                }
                            }
                        });
                    }
                });
            }
        });

        if (allAwards.size === 0) {
            showToastMessage('No awards found to match.', 'error');
            return;
        }

        const awardsToCreate: any[] = [];

        // For each existing award
        allAwards.forEach((award: IAwardCategory) => {
            // Find the original category that contains this award
            const originalCategory = contest.categories?.find((c: any) => {
                if (c.with_tiered_pricing) {
                    // For tiered pricing, check prize_structure payouts
                    if (Array.isArray(c.prize_structure)) {
                        for (const prize of c.prize_structure) {
                            if (Array.isArray(prize.payouts)) {
                                const awardExistsInPayouts = prize.payouts.some((payout: any) => {
                                    const payoutId = typeof payout === 'string' ? payout : payout._id;
                                    return payoutId === award._id;
                                });
                                if (awardExistsInPayouts) {
                                    return true;
                                }
                            }
                        }
                    }
                } else {
                    // For non-tiered pricing, check category_awards array
                    if (Array.isArray(c.category_awards) && c.category_awards.includes(award._id)) {
                        return true;
                    }
                }
                return false;
            });

            if (!originalCategory) {
                return;
            }

            // Get original award's position and tier information
            let originalPosition = '';
            // Get tier_name directly from the award object (it should always be set for tiered awards)
            let originalTierName = (award as any).tier_name || (award as any).tier_label || '';

            if (originalCategory.with_tiered_pricing && originalCategory.prize_structure) {
                for (const prize of originalCategory.prize_structure) {
                    if (Array.isArray(prize.payouts)) {
                        // Check if award exists in payouts (handles both ID strings and full objects)
                        const awardExistsInPayouts = prize.payouts.some((payout: any) => {
                            const payoutId = typeof payout === 'string' ? payout : payout._id;
                            return payoutId === award._id;
                        });
                        if (awardExistsInPayouts) {
                            originalPosition = prize.position;
                            // Ensure we have tier_name (prefer from award object, fallback to prize structure)
                            if (!originalTierName) {
                                originalTierName = (award as any).tier_name || (award as any).tier_label || '';
                            }
                            break;
                        }
                    }
                }
            }

            // Check each category to see if this award exists there
            const categoriesToIterate = contest.categories || [];
            categoriesToIterate.forEach((targetCategory: any) => {
                // Skip the original category
                if (targetCategory._id === originalCategory._id) return;

                // Handle tiered pricing - iterate through positions and match
                if (targetCategory.with_tiered_pricing && targetCategory.prize_structure) {
                    // If original category is also tiered, match by position
                    if (originalCategory.with_tiered_pricing && originalPosition) {
                        // Find the matching position in target category
                        const matchingPosition = targetCategory.prize_structure.find(
                            (prize: any) => prize.position === originalPosition,
                        );

                        if (!matchingPosition) {
                            return; // Skip if position doesn't exist in target category
                        }

                        // Check if tier_name already exists in this position's payouts
                        // Skip if the same tier_name already exists for this position
                        if (originalTierName) {
                            const tierNameExists = Array.isArray(matchingPosition.payouts)
                                ? matchingPosition.payouts.some((payout: any) => {
                                      // Get tier_name from payout (either directly or from allAwards)
                                      let tierName = '';
                                      if (typeof payout === 'object' && payout !== null) {
                                          tierName = payout.tier_name || payout.tier_label || '';
                                      } else {
                                          // If payout is just an ID, find the award in allAwards
                                          const payoutAward = allAwards.get(payout);
                                          if (payoutAward) {
                                              tierName =
                                                  (payoutAward as any).tier_name ||
                                                  (payoutAward as any).tier_label ||
                                                  '';
                                          }
                                      }
                                      return tierName === originalTierName;
                                  })
                                : false;

                            if (tierNameExists) {
                                return; // Skip if tier_name already exists for this position
                            }
                        }

                        // Create award data for this category and position
                        const newAwardData: any = {
                            category_title: award.category_title,
                            prize: award.prize,
                            additional_prize: award.additional_prize ? [...award.additional_prize] : [''],
                            sub_category: award.sub_category || '',
                            category: targetCategory._id,
                            contest: contest._id,
                            position: originalPosition,
                            tier_position: originalPosition,
                        };

                        // Get available tiers for this category
                        const availableTiers = targetCategory.tier_fee || [];

                        // Find which tiers are already used for this position in target category
                        const usedTierLabels = new Set<string>();
                        if (Array.isArray(matchingPosition.payouts)) {
                            matchingPosition.payouts.forEach((payout: any) => {
                                // Handle both cases: payout as ID string or full award object
                                const payoutId = typeof payout === 'string' ? payout : payout._id;

                                // If payout is a full object, get tier_name directly from it
                                if (typeof payout === 'object' && payout !== null) {
                                    const tierLabel = payout.tier_name || payout.tier_label;
                                    if (tierLabel) {
                                        usedTierLabels.add(tierLabel);
                                    }
                                } else {
                                    // If payout is just an ID, find the award in allAwards
                                    const payoutAward = allAwards.get(payoutId);
                                    if (payoutAward) {
                                        const tierLabel =
                                            (payoutAward as any).tier_name || (payoutAward as any).tier_label;
                                        if (tierLabel) {
                                            usedTierLabels.add(tierLabel);
                                        }
                                    }
                                }
                            });
                        }

                        // First, try to match the original award's tier_name if it exists and is not used
                        let selectedTier = null;
                        if (originalTierName) {
                            const matchingTier = availableTiers.find(
                                (tier: any) => tier.label === originalTierName && !usedTierLabels.has(tier.label),
                            );
                            if (matchingTier) {
                                selectedTier = matchingTier;
                            }
                        }

                        // If no matching tier found, find any unused tier
                        if (!selectedTier) {
                            selectedTier = availableTiers.find(
                                (tier: any) => tier.label && !usedTierLabels.has(tier.label),
                            );
                        }

                        if (selectedTier && selectedTier.label) {
                            newAwardData.tier_label = selectedTier.label;
                            newAwardData.tier_name = selectedTier.label;
                        } else if (availableTiers.length > 0) {
                            // Use first available tier if all are used
                            newAwardData.tier_label = availableTiers[0].label || '';
                            newAwardData.tier_name = availableTiers[0].label || '';
                        } else {
                            // No tiers available, skip this category
                            return;
                        }

                        awardsToCreate.push(newAwardData);
                    } else {
                        // Original category is not tiered, but target is - iterate through all positions
                        targetCategory.prize_structure.forEach((targetPrize: any) => {
                            // For non-tiered original awards, we don't have a tier_name to match
                            // So we check if the award ID already exists
                            const awardExistsInPayouts = Array.isArray(targetPrize.payouts)
                                ? targetPrize.payouts.some((payout: any) => {
                                      const payoutId = typeof payout === 'string' ? payout : payout._id;
                                      return payoutId === award._id;
                                  })
                                : false;

                            if (awardExistsInPayouts) return;

                            // Create award data for this position
                            const newAwardData: any = {
                                category_title: award.category_title,
                                prize: award.prize,
                                additional_prize: award.additional_prize ? [...award.additional_prize] : [''],
                                sub_category: award.sub_category || '',
                                category: targetCategory._id,
                                contest: contest._id,
                                position: targetPrize.position,
                                tier_position: targetPrize.position,
                            };

                            // Get available tiers and find unused one
                            const availableTiers = targetCategory.tier_fee || [];
                            const usedTierLabels = new Set<string>();

                            if (Array.isArray(targetPrize.payouts)) {
                                targetPrize.payouts.forEach((payout: any) => {
                                    const payoutId = typeof payout === 'string' ? payout : payout._id;
                                    if (typeof payout === 'object' && payout !== null) {
                                        const tierLabel = payout.tier_name || payout.tier_label;
                                        if (tierLabel) usedTierLabels.add(tierLabel);
                                    } else {
                                        const payoutAward = allAwards.get(payoutId);
                                        if (payoutAward) {
                                            const tierLabel =
                                                (payoutAward as any).tier_name || (payoutAward as any).tier_label;
                                            if (tierLabel) usedTierLabels.add(tierLabel);
                                        }
                                    }
                                });
                            }

                            const unusedTier = availableTiers.find(
                                (tier: any) => tier.label && !usedTierLabels.has(tier.label),
                            );

                            if (unusedTier && unusedTier.label) {
                                newAwardData.tier_label = unusedTier.label;
                                newAwardData.tier_name = unusedTier.label;
                            } else if (availableTiers.length > 0) {
                                newAwardData.tier_label = availableTiers[0].label || '';
                                newAwardData.tier_name = availableTiers[0].label || '';
                            } else {
                                return; // Skip if no tiers available
                            }

                            awardsToCreate.push(newAwardData);
                        });
                    }
                } else {
                    // Target category is not tiered pricing
                    // Check if award already exists in category_awards
                    const awardExists = Array.isArray(targetCategory.category_awards)
                        ? targetCategory.category_awards.includes(award._id)
                        : false;

                    if (awardExists) return;

                    // Create award data for non-tiered category
                    const newAwardData: any = {
                        category_title: award.category_title,
                        prize: award.prize,
                        additional_prize: award.additional_prize ? [...award.additional_prize] : [''],
                        sub_category: award.sub_category || '',
                        category: targetCategory._id,
                        contest: contest._id,
                    };

                    awardsToCreate.push(newAwardData);
                }
            });
        });

        // Create all awards sequentially to ensure each one is assigned before the next
        if (awardsToCreate.length > 0) {
            const createAwardsSequentially = async () => {
                let successCount = 0;
                let errorCount = 0;

                for (const awardData of awardsToCreate) {
                    try {
                        // Dispatch the action to create the award
                        // The saga will automatically assign it to the category (category_awards or prize_structure)
                        dispatch(createAwardCategory(awardData));

                        // Wait for the saga to complete and update the contest
                        await delay(1000); // Give enough time for API call + contest update

                        // Refresh contest data to ensure next award reads the latest state
                        // This ensures the saga gets the updated contest with previous awards
                        try {
                            await dispatchPromise(
                                getContestManage({
                                    contestId: contest._id,
                                    category: 'awards',
                                }),
                            );
                            // Small delay after refresh to ensure state is updated
                            await delay(300);
                        } catch (refreshError) {
                            console.warn('Error refreshing contest data:', refreshError);
                            // Continue even if refresh fails
                        }

                        successCount++;
                    } catch (error) {
                        console.error('Error creating award:', error);
                        errorCount++;
                    }
                }

                if (successCount > 0) {
                    showToastMessage(
                        `Successfully created ${successCount} award(s) across all categories.${
                            errorCount > 0 ? ` ${errorCount} failed.` : ''
                        }`,
                        successCount === awardsToCreate.length ? 'success' : 'warning',
                    );
                } else {
                    showToastMessage('Failed to create awards. Please try again.', 'error');
                }
            };

            createAwardsSequentially();
        } else {
            showToastMessage('All awards already exist in all categories.', 'info');
        }

        setIsMatchAllDialogOpen(false);
    };

    const handleMoreAction = (action: string, awardId: string) => {
        const award = awardCategoryList.find((a: IAwardCategory) => a._id === awardId);
        if (!award) return;

        switch (action) {
            case 'edit': {
                let awardCategory = contest.categories?.find(
                    (c: any) =>
                        Array.isArray(c.category_awards) &&
                        c.category_awards.some(
                            (linked: any) => (typeof linked === 'string' ? linked : linked?._id) === awardId,
                        ),
                );

                if (!awardCategory && award.tier_name) {
                    awardCategory = contest.categories?.find((c: any) =>
                        (c.tier_fee || []).some(
                            (tier: { _id?: string; label?: string }) =>
                                tier._id === award.tier_name || tier.label === award.tier_name,
                        ),
                    );
                }

                setSelectedAward(awardCategory ? prepareAwardCategoryForEdit(award, awardCategory) : award);
                setIsCreateDialogOpen(true);
                break;
            }
            case 'duplicate':
                // Find the category that contains this award
                const duplicateCategory = contest.categories?.find(
                    (c: any) => Array.isArray(c.category_awards) && c.category_awards.includes(awardId),
                );

                if (!duplicateCategory) return;

                // Create a duplicate award object
                const duplicateAward: any = {
                    ...award,
                    _id: undefined, // Remove _id so it's treated as new
                    category_title: award.category_title,
                    prize: award.prize,
                    additional_prize: award.additional_prize ? [...award.additional_prize] : [''],
                    sub_category: award.sub_category || '',
                    category: '',
                    contest: contest._id,
                };

                // Handle tiered pricing
                if (duplicateCategory?.with_tiered_pricing && duplicateCategory.prize_structure) {
                    // Find the position of the original award
                    let originalPosition = '';
                    let originalTierLabel = '';

                    for (const prize of duplicateCategory.prize_structure) {
                        if (Array.isArray(prize.payouts) && prize.payouts.includes(awardId as any)) {
                            originalPosition = prize.position;
                            originalTierLabel = (award as any).tier_name || (award as any).tier_label || '';
                            duplicateAward.position = originalPosition;
                            break;
                        }
                    }

                    // Get all available tiers for this category
                    const availableTiers = duplicateCategory.tier_fee || [];

                    // Find which tiers are already used for this position
                    const usedTierLabels = new Set<string>();
                    if (originalPosition && duplicateCategory.prize_structure) {
                        for (const prize of duplicateCategory.prize_structure) {
                            if (prize.position === originalPosition && Array.isArray(prize.payouts)) {
                                // Find awards in this position and get their tier labels
                                prize.payouts.forEach((payoutId: any) => {
                                    const payoutAward = awardCategoryList.find((a: IAwardCategory) => {
                                        const id = typeof payoutId === 'string' ? payoutId : payoutId._id;
                                        return a._id === id;
                                    });
                                    if (payoutAward) {
                                        const tierLabel =
                                            (payoutAward as any).tier_name || (payoutAward as any).tier_label;
                                        if (tierLabel) {
                                            usedTierLabels.add(tierLabel);
                                        }
                                    }
                                });
                            }
                        }
                    }

                    // Find an unused tier (excluding the original tier)
                    const unusedTier = availableTiers.find(
                        (tier: any) =>
                            tier.label && !usedTierLabels.has(tier.label) && tier.label !== originalTierLabel,
                    );

                    if (unusedTier && unusedTier.label) {
                        duplicateAward.tier_label = unusedTier.label;
                        duplicateAward.tier_name = unusedTier.label;
                        duplicateAward.tier_position = originalPosition;
                    } else {
                        // If all tiers are used, try to find a different tier from the original
                        const differentTier = availableTiers.find(
                            (tier: any) => tier.label && tier.label !== originalTierLabel,
                        );

                        if (differentTier && differentTier.label) {
                            duplicateAward.tier_label = differentTier.label;
                            duplicateAward.tier_name = differentTier.label;
                            duplicateAward.tier_position = originalPosition;
                        } else if (availableTiers.length > 0) {
                            // If no different tier found, use the first available tier
                            duplicateAward.tier_label = availableTiers[0].label || '';
                            duplicateAward.tier_name = availableTiers[0].label || '';
                            duplicateAward.tier_position = originalPosition;
                        } else {
                            // No tiers available, keep original tier_label (user can change it)
                            duplicateAward.tier_label = originalTierLabel;
                            duplicateAward.tier_name = originalTierLabel;
                            duplicateAward.tier_position = originalPosition;
                        }
                    }
                } else {
                    // For non-tiered pricing, just copy the award data
                    duplicateAward.position = (award as any).position || '';
                }

                setSelectedAward(duplicateAward);
                setIsCreateDialogOpen(true);
                break;
            case 'delete':
                // Find the category that contains this award
                const category = contest.categories?.find(
                    (c: any) => Array.isArray(c.category_awards) && c.category_awards.includes(awardId),
                );

                // Find position in prize_structure if tiered pricing is enabled
                let position: string | undefined;
                if (category?.with_tiered_pricing && category.prize_structure) {
                    for (const prize of category.prize_structure) {
                        if (Array.isArray(prize.payouts) && prize.payouts.includes(awardId as any)) {
                            position = prize.position;
                            break;
                        }
                    }
                }

                if (category) {
                    dispatch(
                        deleteAwardCategory({
                            documentId: awardId,
                            payload: {
                                categoryId: category._id,
                                contestId: contest._id,
                                position: position,
                            },
                        }),
                    );
                } else {
                    dispatch(
                        deleteAwardCategory({
                            documentId: awardId,
                            payload: {
                                position: position,
                            },
                        }),
                    );
                }
                break;
            default:
                break;
        }
    };

    const isMatchAllDisabled =
        !!contest.winner_email_sent ||
        awardCategoryList.length === 0 ||
        !contest.categories ||
        contest.categories.length <= 1;

    const matchAllTooltip = contest.winner_email_sent
        ? 'Winner email already sent'
        : awardCategoryList.length === 0
        ? 'Create award categories first'
        : !contest.categories || contest.categories.length <= 1
        ? 'Requires multiple submission categories'
        : 'Match all awards to categories';

    if (!contest.categories || contest.categories.length === 0) {
        return (
            <Box
                sx={
                    isBrand
                        ? { ...brandAwardEmptyStateSx, mt: 2 }
                        : {
                              backgroundColor: theme[themeStyle].secondaryBg,
                              borderRadius: 2,
                              padding: 4,
                              textAlign: 'center',
                          }
                }>
                <Typography
                    sx={
                        isBrand
                            ? brandAwardBodyTextSx
                            : {
                                  fontFamily: FONT_ENUM.MAIN_REGULAR,
                                  fontSize: '1rem',
                                  color: theme[themeStyle].primary_white,
                              }
                    }>
                    Please create submission categories first before creating award categories.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: isBrand ? 2 : 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {!hasSubmissions && awardCategoryList.length > 0 && (
                        <Checkbox
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                            sx={
                                isBrand
                                    ? {
                                          color: midnight.onSurfaceVariant,
                                          '&.Mui-checked': { color: midnight.primary },
                                          '&.MuiCheckbox-indeterminate': { color: midnight.primary },
                                      }
                                    : {
                                          color: theme[themeStyle].primary_white,
                                          '&.Mui-checked': { color: theme[themeStyle].highlight },
                                          '&.MuiCheckbox-indeterminate': {
                                              color: theme[themeStyle].highlight,
                                          },
                                      }
                            }
                        />
                    )}
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: isBrand ? '1.125rem' : '1.25rem',
                            color: isBrand ? midnight.onSurface : theme[themeStyle].primary_white,
                            letterSpacing: isBrand ? '-0.01em' : undefined,
                        }}>
                        Award Categories
                        {selectedAwardIds.size > 0 && (
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                    color: isBrand ? midnight.primary : theme[themeStyle].primary_orange,
                                    ml: 1,
                                }}>
                                ({selectedAwardIds.size} selected)
                            </Typography>
                        )}
                    </Typography>
                </Box>
                {isBrand ? (
                    <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                        {!hasSubmissions && selectedAwardIds.size > 0 && (
                            <Tooltip title={`Delete selected (${selectedAwardIds.size})`}>
                                <IconButton
                                    aria-label={`Delete selected (${selectedAwardIds.size})`}
                                    onClick={() => setIsDeleteSelectedDialogOpen(true)}
                                    size="small"
                                    sx={brandContestDangerIconButtonSx}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Create laurel">
                            <IconButton
                                aria-label="Create laurel"
                                onClick={() => setIsLaurelCreatorOpen(true)}
                                size="small"
                                sx={brandContestSecondaryIconButtonSx}>
                                <EmojiEvents fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {!hasSubmissions && !contest.winner_email_sent && (
                            <Tooltip title="Create award category">
                                <IconButton
                                    aria-label="Create award category"
                                    onClick={() => {
                                        setSelectedAward(null);
                                        setIsCreateDialogOpen(true);
                                    }}
                                    size="small"
                                    sx={brandContestPrimaryIconButtonSx}>
                                    <Add fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={matchAllTooltip}>
                            <span>
                                <IconButton
                                    aria-label="Match all awards"
                                    onClick={() => setIsMatchAllDialogOpen(true)}
                                    disabled={isMatchAllDisabled}
                                    size="small"
                                    sx={brandContestSecondaryIconButtonSx}>
                                    <Sync fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                ) : (
                    <Stack direction="row" spacing={2}>
                        {!hasSubmissions && selectedAwardIds.size > 0 && (
                            <CustomButton
                                customStyle={{
                                    borderRadius: 5,
                                }}
                                buttonProps={{
                                    startIcon: <Delete />,
                                    onClick: () => setIsDeleteSelectedDialogOpen(true),
                                    sx: {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        color: theme[themeStyle].black,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            opacity: 0.9,
                                        },
                                        px: 2,
                                        py: 1,
                                    },
                                }}
                                label={`Delete Selected (${selectedAwardIds.size})`}
                            />
                        )}
                        <CustomButton
                            customStyle={{
                                borderRadius: 5,
                            }}
                            buttonProps={{
                                startIcon: <EmojiEvents />,
                                onClick: () => setIsLaurelCreatorOpen(true),
                                sx: {
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].black,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        opacity: 0.9,
                                    },
                                    px: 2,
                                    py: 1,
                                },
                            }}
                            label="Create Laurel"
                        />
                        {!hasSubmissions && !contest.winner_email_sent && (
                            <>
                                <CustomButton
                                    customStyle={{
                                        borderRadius: 5,
                                    }}
                                    buttonProps={{
                                        startIcon: <Add />,
                                        onClick: () => {
                                            setSelectedAward(null);
                                            setIsCreateDialogOpen(true);
                                        },
                                        sx: {
                                            backgroundColor: theme[themeStyle].highlight,
                                            color: theme[themeStyle].black,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                opacity: 0.9,
                                            },
                                            px: 2,
                                            py: 1,
                                        },
                                    }}
                                    label="Create Award Category"
                                />
                                <CustomButton
                                    customStyle={{
                                        borderRadius: 5,
                                    }}
                                    buttonProps={{
                                        startIcon: <Sync />,
                                        onClick: () => setIsMatchAllDialogOpen(true),
                                        disabled:
                                            awardCategoryList.length === 0 ||
                                            !contest.categories ||
                                            contest.categories.length <= 1,
                                        sx: {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            color: theme[themeStyle].black,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                opacity: 0.9,
                                            },
                                            '&:disabled': {
                                                backgroundColor: theme[themeStyle].placeholderGray,
                                                color: theme[themeStyle].light_gray_text,
                                                opacity: 0.6,
                                            },
                                            px: 2,
                                            py: 1,
                                        },
                                    }}
                                    label="Match All Awards"
                                />
                            </>
                        )}
                    </Stack>
                )}
            </Box>

            {hasSubmissions && (
                <Box
                    sx={
                        isBrand
                            ? brandAwardWarningBannerSx
                            : {
                                  backgroundColor: theme[themeStyle].secondaryBg,
                                  borderRadius: 2,
                                  padding: 2,
                                  mb: 3,
                              }
                    }>
                    {isBrand && (
                        <WarningAmber sx={{ color: midnight.primary, fontSize: 22, mt: 0.15, flexShrink: 0 }} />
                    )}
                    <Typography
                        sx={
                            isBrand
                                ? brandAwardBodyTextSx
                                : {
                                      fontFamily: FONT_ENUM.MAIN_REGULAR,
                                      color: theme[themeStyle].primary_white,
                                  }
                        }>
                        Award amounts cannot be edited after submissions exist, but you can still delete prizes you no
                        longer need (for example Audience Award after dropping voting).
                    </Typography>
                </Box>
            )}

            {!awardCategoryList.length ? (
                <Box
                    sx={
                        isBrand
                            ? brandAwardEmptyStateSx
                            : {
                                  backgroundColor: theme[themeStyle].secondaryBg,
                                  borderRadius: 2,
                                  padding: 4,
                                  textAlign: 'center',
                              }
                    }>
                    <Typography
                        sx={
                            isBrand
                                ? brandAwardBodyTextSx
                                : {
                                      fontFamily: FONT_ENUM.MAIN_REGULAR,
                                      fontSize: '1rem',
                                      color: theme[themeStyle].primary_white,
                                  }
                        }>
                        No award categories have been created yet
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {awardCategoryList.map((award: IAwardCategory) => (
                        <Grid
                            item
                            xs={12}
                            md={isBrand ? 12 : 6}
                            lg={isBrand ? 6 : 4}
                            key={award._id}
                            sx={{ minWidth: 0 }}>
                            <AwardCategoryCard
                                visualVariant={visualVariant}
                                award={award as unknown as TSubmissionCategoryAward}
                                onMoreAction={handleMoreAction}
                                hasSubmissions={hasSubmissions}
                                contest={contest}
                                isSelected={selectedAwardIds.has(award._id)}
                                onSelect={handleSelectAward}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            <CreateAwardCategoryDialog
                open={isCreateDialogOpen}
                visualVariant={visualVariant}
                onClose={() => {
                    setIsCreateDialogOpen(false);
                    setSelectedAward(null);
                }}
                onSubmit={handleCreateAward}
                contest={contest}
                editAward={selectedAward}
            />

            <LaurelCreatorModal
                open={isLaurelCreatorOpen}
                onClose={() => setIsLaurelCreatorOpen(false)}
                onSave={laurelData => {
                    console.log('Laurel saved:', laurelData);
                    // TODO: Implement laurel saving logic
                }}
            />

            <DialogueSheet
                open={isMatchAllDialogOpen}
                onClose={() => setIsMatchAllDialogOpen(false)}
                onAgreeClicked={matchAllAwardsToCategories}
                onDisagreeClick={() => setIsMatchAllDialogOpen(false)}
                header="Match All Awards to Categories"
                body="This will create copies of all existing awards for categories where they don't already exist. For tiered pricing categories, it will automatically select available tiers. Continue?"
                positiveText="Yes, Match All"
                negativeText="Cancel"
            />
            <DialogueSheet
                open={isDeleteSelectedDialogOpen}
                onClose={() => setIsDeleteSelectedDialogOpen(false)}
                onAgreeClicked={handleDeleteSelected}
                onDisagreeClick={() => setIsDeleteSelectedDialogOpen(false)}
                header="Delete Selected Awards"
                body={`Are you sure you want to delete ${selectedAwardIds.size} award(s)? This action cannot be undone.`}
                positiveText="Yes, Delete"
                negativeText="Cancel"
            />
        </Box>
    );
};
